/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, useState, ReactNode, useEffect } from "react";
import type { TenantContext } from "@/lib/tenant-context";
import { setTenantQueryKey } from "@/lib/tenant-query-key";

interface TenantContextValue {
  baseContext: TenantContext | null;
  overrideContext: TenantContext | null;
  tenantContext: TenantContext | null;
  setBaseContext: (context: TenantContext | null) => void;
  setOverrideContext: (context: TenantContext | null) => void;
  clearOverrideContext: () => void;
}

const TenantContextStore = createContext<TenantContextValue | undefined>(undefined);

export function TenantContextProvider({ children }: { children: ReactNode }) {
  const [baseContext, setBaseContextState] = useState<TenantContext | null>(null);
  const [overrideContext, setOverrideContextState] = useState<TenantContext | null>(null);

  const areTenantContextsEquivalent = useCallback((a: TenantContext | null, b: TenantContext | null) => {
    if (a === null || b === null) {
      return a === b;
    }
    return (
      a.organizationId === b.organizationId &&
      a.organizationName === b.organizationName &&
      a.supportSessionId === b.supportSessionId &&
      a.source === b.source
    );
  }, []);

  const setBaseContext = useCallback((context: TenantContext | null) => {
    setBaseContextState((prev) =>
      areTenantContextsEquivalent(prev, context) ? prev : context
    );
  }, [areTenantContextsEquivalent]);

  const setOverrideContext = useCallback((context: TenantContext | null) => {
    setOverrideContextState((prev) =>
      areTenantContextsEquivalent(prev, context) ? prev : context
    );
  }, [areTenantContextsEquivalent]);

  const clearOverrideContext = useCallback(() => {
    setOverrideContextState((prev) => (prev === null ? prev : null));
  }, []);

  const tenantContext = useMemo(() => overrideContext ?? baseContext, [overrideContext, baseContext]);
  useEffect(() => {
    setTenantQueryKey(tenantContext);
  }, [tenantContext]);

  return (
    <TenantContextStore.Provider
      value={{
        baseContext,
        overrideContext,
        tenantContext,
        setBaseContext,
        setOverrideContext,
        clearOverrideContext,
      }}
    >
      {children}
    </TenantContextStore.Provider>
  );
}

export function useTenantContext() {
  const context = useContext(TenantContextStore);
  if (!context) {
    throw new Error("useTenantContext must be used within a TenantContextProvider");
  }
  return context;
}

export function useOptionalTenantContext() {
  return useContext(TenantContextStore) ?? null;
}

/**
 * useTenantQueryKey for a hook that can also run outside a TenantContextProvider: the same
 * ["tenant", organizationId, supportSessionId] tuple, with nulls when there is no provider.
 * A query keyed on it moves to a new cache entry when a support session starts or ends,
 * instead of relying on resetTenantQueries() alone (#6421).
 */
export function useOptionalTenantQueryKey() {
  const tenantContext = useOptionalTenantContext()?.tenantContext;
  return useMemo(
    () => ["tenant", tenantContext?.organizationId ?? null, tenantContext?.supportSessionId ?? null] as const,
    [tenantContext?.organizationId, tenantContext?.supportSessionId],
  );
}

export function useTenantQueryKey() {
  const { tenantContext } = useTenantContext();
  return useMemo(
    () => ["tenant", tenantContext?.organizationId ?? null, tenantContext?.supportSessionId ?? null] as const,
    [tenantContext?.organizationId, tenantContext?.supportSessionId],
  );
}